/**
 * AddBuildingDialog — the redesigned new-building modal.
 *
 * Replaces the dual-tab `NewBuildingDialog`. Owns the four-step state
 * machine (Identify → Verify CBL → Connect data → Confirm owner) and the
 * draft that accumulates across steps.
 *
 * Launch contract is preserved: `<AddBuildingDialog isOpen onOpenChange />`
 * matches what `Dashboard/Page.tsx:261` and `BuildingsList/Page.tsx:475`
 * already pass. The old `NewBuildingDialog` module re-exports this as a
 * named + default export so existing call sites don't need touching.
 *
 * A11y:
 *   - shadcn `Dialog` provides focus trap and Escape.
 *   - Each step component focuses its `<h2>` on mount.
 *   - Escape-with-dirty-state prompt is handled via `onOpenChange` below.
 *   - Progress is expressed in a visible step rail and an aria-live
 *     announcement in the dialog header.
 */

import React, { useCallback, useMemo, useReducer, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/shared/ui/dialog";
import { cn } from "@/lib/utils";
import api from "@/api/base";
import { toast } from "@/queries/useToast";
import IdentifyStep from "./IdentifyStep";
import VerifyCblStep from "./VerifyCblStep";
import ConnectDataStep from "./ConnectDataStep";
import ConfirmOwnerStep from "./ConfirmOwnerStep";
import {
  INITIAL_DRAFT,
  STEP_META,
  STEP_ORDER,
  type AddBuildingConfirmOwnerForm,
  type AddBuildingDraft,
  type AddBuildingEspmSelection,
  type AddBuildingStepId,
  type CblPreviewMatch,
} from "./types";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: "RESET" }
  | { type: "SET_STEP"; step: AddBuildingStepId }
  | { type: "SET_SEARCH_TERM"; value: string }
  | { type: "SUBMIT_SEARCH" }
  | {
      type: "SELECT_MATCH";
      match: CblPreviewMatch;
      /**
       * Total candidate count at the moment of selection. Used to populate
       * `wasAmbiguous` (true when > 1) and `originalCandidateCount` for D1's
       * recovery banner. Defaults to 1 if omitted (back-compat with callers
       * that haven't been updated yet — auto-advance always passes 1).
       */
      candidateCount?: number;
    }
  | { type: "CLEAR_MATCH" }
  | { type: "SELECT_ESPM"; selection: AddBuildingEspmSelection | null }
  | { type: "SKIP_ESPM" }
  | { type: "PATCH_OWNER_FORM"; patch: Partial<AddBuildingConfirmOwnerForm> };

// Exported for unit-testing the SELECT_MATCH plumbing (F2). The reducer is
// pure, so testing it in isolation is cheaper than threading the dialog tree.
export type AddBuildingAction = Action;
export function addBuildingReducer(
  state: AddBuildingDraft,
  action: Action,
): AddBuildingDraft {
  return reducer(state, action);
}

function reducer(state: AddBuildingDraft, action: Action): AddBuildingDraft {
  switch (action.type) {
    case "RESET":
      return INITIAL_DRAFT;
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SET_SEARCH_TERM":
      // Update the controlled input only; do NOT clear ``submittedTerm``
      // so the user can edit freely while prior results stay visible.
      // They re-submit (Enter / Search button) to refresh. Clearing
      // the input back to empty also clears submittedTerm so stale
      // results vanish.
      return {
        ...state,
        identify: {
          ...state.identify,
          searchTerm: action.value,
          submittedTerm:
            action.value.trim().length === 0
              ? ""
              : state.identify.submittedTerm,
        },
      };
    case "SUBMIT_SEARCH":
      // Copy the current input into submittedTerm to trigger a fetch
      // in ``useCblPreview``. Trim so an accidental trailing space
      // doesn't bust the queryKey cache from a prior identical submit.
      return {
        ...state,
        identify: {
          ...state.identify,
          submittedTerm: state.identify.searchTerm.trim(),
        },
      };
    case "SELECT_MATCH": {
      const count = action.candidateCount ?? 1;
      return {
        ...state,
        selectedMatch: action.match,
        step: "verifyCbl",
        wasAmbiguous: count > 1,
        originalCandidateCount: count,
      };
    }
    case "CLEAR_MATCH":
      // Back to Step 1; ambiguity flag clears too — if the user re-picks a
      // single-match resolver hit we shouldn't show the D1 banner.
      return {
        ...state,
        selectedMatch: null,
        step: "identify",
        wasAmbiguous: false,
        originalCandidateCount: 0,
      };
    case "SELECT_ESPM":
      return {
        ...state,
        espmSelection: action.selection,
        // Selecting a property cancels a prior skip.
        espmSkipped: false,
      };
    case "SKIP_ESPM":
      return { ...state, espmSkipped: true, espmSelection: null };
    case "PATCH_OWNER_FORM":
      return {
        ...state,
        confirmOwner: { ...state.confirmOwner, ...action.patch },
      };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Step rail
// ---------------------------------------------------------------------------

const StepRail: React.FC<{ currentStep: AddBuildingStepId }> = ({
  currentStep,
}) => {
  const currentIdx = STEP_ORDER.indexOf(currentStep);
  return (
    <ol
      aria-label="Progress"
      className="hidden md:flex flex-col gap-1 w-[200px] shrink-0 pr-6 border-r border-border"
    >
      {STEP_ORDER.map((id, idx) => {
        const meta = STEP_META[id];
        const isCurrent = id === currentStep;
        const isDone = idx < currentIdx;
        return (
          <li
            key={id}
            aria-current={isCurrent ? "step" : undefined}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-2 text-sm",
              isCurrent && "bg-accent",
              !isCurrent && isDone && "text-muted-foreground",
              !isCurrent && !isDone && "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "inline-flex items-center justify-center h-6 w-6 rounded-full border text-xs",
                isCurrent &&
                  "border-primary bg-primary text-primary-foreground",
                isDone && "border-primary/50 bg-primary/10 text-primary",
                !isCurrent && !isDone && "border-border",
              )}
              aria-hidden="true"
            >
              {idx + 1}
            </span>
            <span className={cn(isCurrent && "font-medium text-foreground")}>
              {meta.shortTitle}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const MobileStepProgress: React.FC<{ currentStep: AddBuildingStepId }> = ({
  currentStep,
}) => {
  const currentIdx = STEP_ORDER.indexOf(currentStep);
  const meta = STEP_META[currentStep];
  return (
    <div className="md:hidden flex flex-col gap-1 pb-3">
      <div className="flex gap-1" aria-hidden="true">
        {STEP_ORDER.map((id, idx) => (
          <div
            key={id}
            className={cn(
              "h-1.5 rounded-full flex-1",
              idx <= currentIdx ? "bg-primary" : "bg-muted",
            )}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Step {currentIdx + 1} of {STEP_ORDER.length}: {meta.shortTitle}
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

interface AddBuildingDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const AddBuildingDialog: React.FC<AddBuildingDialogProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const [draft, dispatch] = useReducer(reducer, INITIAL_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isDirty = useMemo(() => {
    if (draft.identify.searchTerm.trim().length > 0) return true;
    if (draft.selectedMatch) return true;
    if (draft.espmSelection) return true;
    if (draft.confirmOwner.legalOwnerEntity.trim().length > 0) return true;
    if (draft.confirmOwner.signerRelationship !== null) return true;
    return false;
  }, [draft]);

  const handleClose = useCallback(() => {
    dispatch({ type: "RESET" });
    setSubmitting(false);
    setSubmitError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const requestClose = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true);
        return;
      }
      if (submitting) return;
      if (!isDirty) {
        handleClose();
        return;
      }
      // Lightweight confirm. Not a full-screen dialog-in-dialog — the
      // native confirm is a11y-accessible and respects prefers-reduced-motion.
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        "Discard this building? You haven't finished adding it.",
      );
      if (confirmed) handleClose();
    },
    [isDirty, submitting, handleClose, onOpenChange],
  );

  const handleContinueFromVerify = () => {
    // All three outcomes advance to Step 3 (Connect data). Delisted and
    // never-on-CBL buildings still benefit from ESPM linking for L84 /
    // future-cycle tracking.
    dispatch({ type: "SET_STEP", step: "connectData" });
  };

  const handleSubmit = useCallback(async () => {
    if (!draft.selectedMatch) return;
    setSubmitting(true);
    setSubmitError(null);
    const match = draft.selectedMatch;
    const owner = draft.confirmOwner;
    try {
      // The existing `POST /buildings/` requires `name` and `nyc_bin`. The
      // redesign adds owner-verification fields; the backend agent is
      // wiring them (new-optional contract). If the backend hasn't shipped
      // the new fields yet, they're ignored — the building still gets
      // created with the legacy shape.
      //
      // Multipart path is only used when there's a supporting-doc file,
      // since the legacy endpoint accepts JSON. Backend agent will upgrade
      // to multipart-aware.
      const bin =
        match.identity.bin ??
        // Fallback: when never_on_cbl has no BIN, pass an empty string
        // so the backend can reject (or handle EXEMPT_NO_CBL flow as per
        // the locked-in design).
        "";

      const base = {
        // Send the resolved address when we have one; otherwise omit
        // the field and let the server's resolver chain (B4) populate
        // it. Never send the raw searchTerm (BIN/BBL) — that landed
        // numeric identifiers in the DB as building titles.
        name: match.identity.address ?? "",
        nyc_bin: bin,
        nyc_bbl: match.identity.bbl ?? undefined,
        cbl_outcome: match.outcome,
        legal_owner_entity: owner.legalOwnerEntity.trim(),
        signer_relationship: owner.signerRelationship,
        signer_justification: owner.justification.trim() || null,
        onboarding_intent:
          match.outcome === "on_2026_cbl" ? "primary" : "exempt_ack",
        espm_property_id: draft.espmSelection?.propertyId ?? null,
      };

      interface CreatedBuilding {
        id?: string;
        name?: string;
      }
      let building: CreatedBuilding | null = null;
      if (owner.supportingDoc) {
        const fd = new FormData();
        Object.entries(base).forEach(([k, v]) => {
          if (v === null || v === undefined) return;
          fd.append(k, String(v));
        });
        fd.append("signer_supporting_doc", owner.supportingDoc);
        const res = await api.post<CreatedBuilding>("buildings/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        building = res.data;
      } else {
        const res = await api.post<CreatedBuilding>("buildings/", base);
        building = res.data;
      }

      queryClient.invalidateQueries();

      if (!building?.id) {
        throw new Error("Server did not return a building ID.");
      }

      toast.success({
        heading: "Building added",
        // Phase 1.B copy truth-up: the cross-reference doesn't run at
        // add-building time and there is no background queue — it's
        // invoked from `OwnerVerificationBanner` at signature time.
        // Don't reintroduce "queued" copy without a real worker behind it.
        body: "Owner cross-reference will run at signature time.",
      });

      handleClose();
      navigate({
        to: "/buildings/$buildingId",
        params: { buildingId: building.id },
      });
    } catch (err: unknown) {
      const message = extractErrorMessage(err);
      setSubmitError(message);
      setSubmitting(false);
    }
  }, [draft, handleClose, navigate, queryClient]);

  const { step } = draft;
  const currentMeta = STEP_META[step];

  return (
    <Dialog open={isOpen} onOpenChange={requestClose}>
      <DialogContent
        className="max-h-[90vh] max-w-[820px] w-[min(820px,calc(100vw-2rem))] p-0 flex flex-col"
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <div className="flex flex-col md:flex-row min-h-0 flex-1">
          <div className="p-6 md:pr-0 md:pt-6 md:pb-6 md:pl-6">
            <DialogTitle className="text-base font-semibold mb-4">
              Add a building
            </DialogTitle>
            <StepRail currentStep={step} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-6 md:pl-6">
            <MobileStepProgress currentStep={step} />
            <span aria-live="polite" className="sr-only">
              Step {STEP_ORDER.indexOf(step) + 1} of {STEP_ORDER.length}:{" "}
              {currentMeta.title}
            </span>

            {step === "identify" && (
              <IdentifyStep
                draft={draft}
                onSearchTermChange={(value) =>
                  dispatch({ type: "SET_SEARCH_TERM", value })
                }
                onSubmitSearch={() => dispatch({ type: "SUBMIT_SEARCH" })}
                onSelectMatch={(match, candidateCount) =>
                  dispatch({ type: "SELECT_MATCH", match, candidateCount })
                }
                onCancel={() => requestClose(false)}
              />
            )}
            {step === "verifyCbl" && draft.selectedMatch && (
              <VerifyCblStep
                match={draft.selectedMatch}
                onBack={() => dispatch({ type: "CLEAR_MATCH" })}
                onContinue={handleContinueFromVerify}
                onClose={handleClose}
                wasAmbiguous={draft.wasAmbiguous}
                originalCandidateCount={draft.originalCandidateCount}
                // D1 — the "Wrong building?" link in the recovery banner
                // dispatches the same action as the panel's Back button
                // today. Kept as a separate prop so future divergence
                // (e.g. analytics event) doesn't require touching both
                // call sites.
                onRestartSearch={() => dispatch({ type: "CLEAR_MATCH" })}
              />
            )}
            {step === "connectData" && (
              <ConnectDataStep
                draft={draft}
                onBack={() => dispatch({ type: "SET_STEP", step: "verifyCbl" })}
                onContinue={() =>
                  dispatch({ type: "SET_STEP", step: "confirmOwner" })
                }
                onSelectEspm={(selection) =>
                  dispatch({ type: "SELECT_ESPM", selection })
                }
                onSkipEspm={() => {
                  dispatch({ type: "SKIP_ESPM" });
                  dispatch({ type: "SET_STEP", step: "confirmOwner" });
                }}
              />
            )}
            {step === "confirmOwner" && (
              <ConfirmOwnerStep
                draft={draft}
                submitting={submitting}
                submitError={submitError}
                onBack={() =>
                  dispatch({ type: "SET_STEP", step: "connectData" })
                }
                onChangeOwnerForm={(patch) =>
                  dispatch({ type: "PATCH_OWNER_FORM", patch })
                }
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Error message extraction
// ---------------------------------------------------------------------------

function extractErrorMessage(err: unknown): string {
  if (!err) return "Something went wrong.";
  const maybeAxios = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = maybeAxios.response?.status;
  const data = maybeAxios.response?.data;
  if (status === 409) {
    return "This building is already in your portfolio. Open it from the Buildings list.";
  }
  if (status === 400 && data && typeof data === "object") {
    // DRF error shape: {field: [msg]} — join the first message.
    const values = Object.values(data as Record<string, unknown>);
    const firstMessage = values.reduce<string | null>((acc, value) => {
      if (acc) return acc;
      if (Array.isArray(value) && value.length > 0) {
        const first = value[0];
        return typeof first === "string" ? first : acc;
      }
      return typeof value === "string" ? value : acc;
    }, null);
    if (firstMessage) return firstMessage;
  }
  if (maybeAxios.message) return maybeAxios.message;
  return "Something went wrong creating this building.";
}

export default AddBuildingDialog;
